/**
 * PlugDrawer Component - Device details drawer with logs and alerts
 * 
 * Features:
 * - Device information display with status and metadata
 * - Advanced date range search for logs and alerts
 * - Sortable tables for logs and alerts data
 * - Real-time data refresh capabilities
 * - Error handling for API failures and no data scenarios
 * - Pagination for large datasets
 * - Date validation (max 30 days range)
 * 
 * @author CTS - Ford PM UI Team
 
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { getLogsApi, getAlertsApi } from '../../api/dashboardApi';
import {
  PlugDrawerContainer,
  PlugDrawerHeader,
  PlugDrawerTitle,
  PlugDrawerDeviceName,
  PlugDrawerContent,
  PlugDrawerMetaSection,
  PlugDrawerDateFiltersPanel,
  PlugDrawerDateFiltersContainer,
  PlugDrawerDateFiltersLeft,
  PlugDrawerDateFiltersRight,
  PlugDrawerRangeText,
  PlugDrawerPrimaryButton,
  PlugDrawerSecondaryButton,
  // PlugDrawerTabNavigation,
  // PlugDrawerTabButton,
  PlugDrawerTabContentPanel,
  PlugDrawerTabContentHeader,
  PlugDrawerTableWrapper,
  PlugDrawerLoadingMessage,
  PlugDrawerSpinner,
  PlugDrawerErrorBox,
  PlugDrawerErrorHeader,
  PlugDrawerErrorMessage,
  PlugDrawerErrorHelp,
  PlugDrawerPagination,
  PlugDrawerPaginationText,
  // PlugDrawerEmptyState,
  PlugDrawerAdvancedSearchToggle,
  PlugDrawerAdvancedSearchContainer,
  PlugDeviceDetailRowContainer,
  PlugDrawerDateFieldLabel
} from './PlugDrawer.styles';
import { DashboardMonospaceText } from './Dashboard.styles';
import { Typography, Box } from '@mui/material';
import StatusIndicator from '../../components/StatusIndicator.jsx';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Search as SearchIcon, ArrowUpward, ArrowDownward, UnfoldMore, Close as CloseIcon } from '@mui/icons-material';
import { AppLabels } from '../../constant/constant';
import DateUtils from '../../utils/DateUtils.jsx';
import { renderCriticalDeviceList, formattedText } from '../../utils/TextTooltipUtils.jsx';

// Reusable metadata field component to eliminate duplication
const PlugMetadataField = ({ label, value, isStatus = false }) => (
  <PlugDeviceDetailRowContainer>
    <Typography variant="caption" className="detail-label">
      {label}
    </Typography>
    {isStatus ? (
      <StatusIndicator status={value || AppLabels.notApplicable} />
    ) : (
      <Typography variant="body2" className="detail-value">
        {value || AppLabels.notApplicable}
      </Typography>
    )}
  </PlugDeviceDetailRowContainer>
);

PlugMetadataField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isStatus: PropTypes.bool
};

PlugMetadataField.defaultProps = {
  value: null,
  isStatus: false
};

// Constants to avoid magic numbers
const DATE_CONFIG = {
  DEFAULT_DAYS_BACK: 7,
  HOURS_PER_DAY: 24,
  MINUTES_PER_HOUR: 60,
  SECONDS_PER_MINUTE: 60,
  MILLISECONDS_PER_SECOND: 1000,
  MAX_DATE_RANGE_DAYS: 30
};

const PAGINATION_CONFIG = {
  LOGS_PAGE_SIZE: 20,
  ALERTS_PAGE_SIZE: 20,
  INITIAL_PAGE: 1
};

const STYLE_CONFIG = {
  FONT_SIZE_SMALL: '12px',
  PADDING_MEDIUM: '20px',
  COLSPAN_FULL: 6
};


/**
 * SortableHeader Component - Clickable table header with sort indicators
 * 
 * @param {React.ReactNode} children - Header text content
 * @param {string} field - Field name for sorting
 * @param {string} sortField - Currently active sort field
 * @param {string} sortDirection - Current sort direction (asc/desc)
 * @param {Function} onSort - Callback function for sort changes
 */
const SortableHeader = ({ children, field, sortField, sortDirection, onSort }) => {
  const handleSort = () => {
    if (sortField === field) {
      // Toggle direction if same field
      onSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      onSort(field, 'asc');
    }
  };

  const getSortIcon = () => {
    if (sortField !== field) {
      return <UnfoldMore sx={{ fontSize: '16px', opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpward sx={{ fontSize: '16px' }} />
      : <ArrowDownward sx={{ fontSize: '16px' }} />;
  };

  return (
    <th
      onClick={handleSort}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '4px'
      }}>
        {children}
        {getSortIcon()}
      </div>
    </th>
  );
};

// PropTypes validation for SortableHeader component
SortableHeader.propTypes = {
  children: PropTypes.node.isRequired,
  field: PropTypes.string.isRequired,
  sortField: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSort: PropTypes.func.isRequired
};

// Default props for SortableHeader component
SortableHeader.defaultProps = {
  sortField: null
};

// Helper function to handle null/undefined values in sorting
const handleNullValues = (aVal, bVal, direction) => {
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return direction === 'asc' ? 1 : -1;
  if (bVal == null) return direction === 'asc' ? -1 : 1;
  return null; // Continue with other comparisons
};

// Helper function to check if field is a date field
const isDateField = (field) => {
  return field.includes('time') || field.includes('at');
};

// Helper function to compare date values
const compareDates = (aVal, bVal, direction) => {
  const aTime = new Date(aVal).getTime();
  const bTime = new Date(bVal).getTime();

  // Check if dates are valid
  if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
    return null; // Fall back to string comparison
  }

  if (aTime < bTime) return direction === 'asc' ? -1 : 1;
  if (aTime > bTime) return direction === 'asc' ? 1 : -1;
  return 0;
};

// Helper function to compare string values
const compareStrings = (aVal, bVal, direction) => {
  const aStr = String(aVal).toLowerCase();
  const bStr = String(bVal).toLowerCase();

  if (aStr < bStr) return direction === 'asc' ? -1 : 1;
  if (aStr > bStr) return direction === 'asc' ? 1 : -1;
  return 0;
};

// Generic sorting function with reduced cognitive complexity
const sortData = (data, field, direction) => {
  return [...data].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];

    // Handle null/undefined values first
    const nullResult = handleNullValues(aVal, bVal, direction);
    if (nullResult !== null) return nullResult;

    // Handle date fields
    if (isDateField(field)) {
      const dateResult = compareDates(aVal, bVal, direction);
      if (dateResult !== null) return dateResult;
    }

    // Fall back to string comparison
    return compareStrings(aVal, bVal, direction);
  });
};

/**
 * Helper functions to create date parameters for API calls
 */

/**
 * Creates date parameters with timestamp range for API calls
 * 
 * @param {string} fromDate - Start date string
 * @param {string} toDate - End date string
 * @param {Function} formatToApiDateTime - Date formatting function
 * @returns {Object} Date parameters object with timestamps
 */
const createDateRangeParams = (fromDate, toDate, formatToApiDateTime) => {
  return {
    startTimestamp: formatToApiDateTime(fromDate),
    endTimestamp: formatToApiDateTime(toDate)
  };
};


/**
 * Reusable Error Display Component
 * 
 * @param {string} error - Error message
 * @param {string} noDataText - Text to show when no data found
 * @param {string} errorPrefix - Prefix for error messages
 */
const ErrorDisplay = ({ error, noDataText = 'No data found', errorPrefix = 'Error loading data: ' }) => {
  const isNoDataError = error.includes(noDataText);

  return (
    <Box sx={{
      marginBottom: '16px',
      padding: '16px',
      borderRadius: '8px',
      textAlign: 'center',
      backgroundColor: isNoDataError ? 'rgba(156, 163, 175, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      border: isNoDataError ? '1px solid rgba(156, 163, 175, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'
    }}>
      <Typography sx={{
        color: isNoDataError ? '#6b7280' : '#ef4444',
        fontSize: '0.95rem'
      }}>
        {isNoDataError ? error : `${errorPrefix}${error}`}
      </Typography>
    </Box>
  );
};

/**
 * Reusable Pagination Component
 * 
 * @param {number} currentPage - Current page number
 * @param {Function} setPage - Function to set page
 * @param {Array} data - Data array for calculating total pages
 * @param {number} pageSize - Items per page
 */
const PaginationControls = ({ currentPage, setPage, data, pageSize }) => {
  const totalPages = Math.ceil(data.length / pageSize);

  return (
    <PlugDrawerPagination>
      <PlugDrawerSecondaryButton
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        {AppLabels.plugDrawerLabels.previousButton}
      </PlugDrawerSecondaryButton>
      <PlugDrawerPaginationText>
        {AppLabels.plugDrawerLabels.pagePrefix}{currentPage}{AppLabels.plugDrawerLabels.pageOfSeparator}{totalPages}
      </PlugDrawerPaginationText>
      <PlugDrawerSecondaryButton
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      >
        {AppLabels.plugDrawerLabels.nextButton}
      </PlugDrawerSecondaryButton>
    </PlugDrawerPagination>
  );
};

// PropTypes for ErrorDisplay component
ErrorDisplay.propTypes = {
  error: PropTypes.string.isRequired,
  noDataText: PropTypes.string,
  errorPrefix: PropTypes.string
};

// PropTypes for PaginationControls component
PaginationControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  pageSize: PropTypes.number.isRequired
};

/**
 * PlugDrawer Main Component - Device details drawer with logs and alerts
 * 
 * @param {string} plugId - Device ID to display details for
 * @param {Object} plugDetails - Device details from parent component
 * @param {Function} onClose - Callback to close the drawer
 */
const PlugDrawer = ({
  plugId,
  plugDetails,
  onClose
}) => {

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  // Device details state (currently using plugDetails prop)
  const [loading] = useState(false);
  const [error] = useState(null);

  // Ref to track if initial fetch has been done to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // State for logs and alerts
  // const [activeTab, setActiveTab] = useState('alerts'); // 'heartbeat', 'logs', 'alerts'
  const activeTab = 'alerts';
  const [logsData, setLogsData] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsPage, setLogsPage] = useState(PAGINATION_CONFIG.INITIAL_PAGE);
  const logsPageSize = PAGINATION_CONFIG.LOGS_PAGE_SIZE;

  // State for alerts
  const [alertsData, setAlertsData] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [alertsPage, setAlertsPage] = useState(PAGINATION_CONFIG.INITIAL_PAGE);
  const alertsPageSize = PAGINATION_CONFIG.ALERTS_PAGE_SIZE;

  // Sorting state for logs
  const [logsSortField, setLogsSortField] = useState(null);
  const [logsSortDirection, setLogsSortDirection] = useState('asc');

  // Sorting state for alerts
  const [alertsSortField, setAlertsSortField] = useState(null);
  const [alertsSortDirection, setAlertsSortDirection] = useState('asc');

  // Date range state (default last 7 days)
  const today = new Date();
  const defaultFrom = new Date(
    Date.now() -
    DATE_CONFIG.DEFAULT_DAYS_BACK *
    DATE_CONFIG.HOURS_PER_DAY *
    DATE_CONFIG.MINUTES_PER_HOUR *
    DATE_CONFIG.SECONDS_PER_MINUTE *
    DATE_CONFIG.MILLISECONDS_PER_SECOND
  );

  // Helper function to format date to local datetime-local format (for UI display)
  const formatToLocalDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Helper function to format date to API format (date and time)
  const formatToApiDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    // Convert "YYYY-MM-DD HH:MM" to "YYYY-MM-DD HH:MM:SS" format for API
    const [datePart, timePart] = dateTimeString.split(' ');
    return `${datePart} ${timePart}:00`;
  };

  const [fromDate, setFromDate] = useState(formatToLocalDateTime(defaultFrom));
  const [toDate, setToDate] = useState(formatToLocalDateTime(today));

  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [dateRangeError, setDateRangeError] = useState('');



  // Helper function to validate date range (max 30 days)
  const validateDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (
      DATE_CONFIG.MILLISECONDS_PER_SECOND *
      DATE_CONFIG.SECONDS_PER_MINUTE *
      DATE_CONFIG.MINUTES_PER_HOUR *
      DATE_CONFIG.HOURS_PER_DAY
    ));
    return diffDays <= DATE_CONFIG.MAX_DATE_RANGE_DAYS;
  };

  // Handle from date change with validation
  const handleFromDateChange = (newFromDate) => {
    setFromDate(newFromDate);

    if (toDate) {
      const fromDateTime = new Date(newFromDate);
      const toDateTime = new Date(toDate);

      // Check if from date is after to date
      if (fromDateTime > toDateTime) {
        setDateRangeError(AppLabels.plugDrawerLabels.fromDateAfterToDate);
        return;
      }

      // Check if date range exceeds maximum allowed days
      if (!validateDateRange(newFromDate, toDate)) {
        setDateRangeError(AppLabels.plugDrawerLabels.dateRangeError);
        return;
      }
    }

    setDateRangeError('');
  };

  // Handle to date change with validation
  const handleToDateChange = (newToDate) => {
    setToDate(newToDate);

    if (fromDate) {
      const fromDateTime = new Date(fromDate);
      const toDateTime = new Date(newToDate);

      // Check if from date is after to date
      if (fromDateTime > toDateTime) {
        setDateRangeError(AppLabels.plugDrawerLabels.fromDateAfterToDate);
        return;
      }

      // Check if date range exceeds maximum allowed days
      if (!validateDateRange(fromDate, newToDate)) {
        setDateRangeError(AppLabels.plugDrawerLabels.dateRangeError);
        // Auto-adjust fromDate to be maximum allowed days before toDate
        const maxFromDate = new Date(newToDate);
        maxFromDate.setDate(maxFromDate.getDate() - DATE_CONFIG.MAX_DATE_RANGE_DAYS);
        setFromDate(formatToLocalDateTime(maxFromDate));
        return;
      }
    }

    setDateRangeError('');
  };

  // Function to fetch device details by plug ID
  // const fetchDeviceDetails = async (id) => {
  //   try {
  //     setLoading(true);
  //     setError(null);
  //     const deviceDetails = await getMockDeviceByIdApi(id);
  //     setPlug(deviceDetails);
  //   } catch (err) {
  //     console.error('Error fetching device details:', err);
  //     setError(err.message);
  //     // Set a fallback plug object to prevent crashes
  //     setPlug({
  //       store_plug_id: id,
  //       id: id,
  //       hasError: true,
  //       errorMessage: err.message
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // ========================================
  // API FUNCTIONS
  // ========================================

  /**
   * Fetches logs data for the specified device with optional date filtering
   * 
   * @param {string} id - Device ID
   * @param {Object} dateParams - Optional date range parameters
   */
  const fetchLogsData = async (id, dateParams = {}) => {
    try {
      setLogsLoading(true);
      setLogsError(null);

      // Use real API with date parameters if provided
      const apiParams = {
        page: 1,
        page_size: 500,
        sort_by: 'last_updated_at',
        sort_order: 'desc',
        ...dateParams
      };

      const logsResponse = await getLogsApi(id, apiParams);
      setLogsData(logsResponse.devices || logsResponse.data || []);
    } catch (err) {
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  // Function to fetch alerts data
  const fetchAlertsData = useCallback(async (id, dateParams = {}) => {
    try {
      setAlertsLoading(true);
      setAlertsError(null);

      // Use real API with date parameters if provided
      const apiParams = {
        page: 1,
        page_size: 100,
        sort_by: 'reported_time',
        sort_order: 'desc',
        ...dateParams
      };

      const alertsResponse = await getAlertsApi(id, apiParams);
      setAlertsData(alertsResponse.incidents || []);
    } catch (err) {
      setAlertsError(err.message);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  // Fetch device details when plugId changes
  useEffect(() => {
    if (plugId && !isFetchingRef.current) {
      isFetchingRef.current = true;

      // Fetch data and reset flag when done
      fetchAlertsData(plugId).finally(() => {
        isFetchingRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugId]); // fetchAlertsData is stable due to useCallback with empty deps

  // Reset tab pages when activeTab changes
  useEffect(() => {
    if (activeTab === 'logs') {
      setLogsPage(PAGINATION_CONFIG.INITIAL_PAGE);
    } else if (activeTab === 'alerts') {
      setAlertsPage(PAGINATION_CONFIG.INITIAL_PAGE);
    }
  }, [activeTab]);

  // Sorting handlers
  const handleLogsSort = (field, direction) => {
    setLogsSortField(field);
    setLogsSortDirection(direction);
    setLogsPage(PAGINATION_CONFIG.INITIAL_PAGE); // Reset to first page when sorting
  };

  const handleAlertsSort = (field, direction) => {
    setAlertsSortField(field);
    setAlertsSortDirection(direction);
    setAlertsPage(PAGINATION_CONFIG.INITIAL_PAGE); // Reset to first page when sorting
  };

  // Get sorted data
  const getSortedLogsData = () => {
    if (!logsSortField) return logsData;
    return sortData(logsData, logsSortField, logsSortDirection);
  };

  const getSortedAlertsData = () => {
    if (!alertsSortField) return alertsData;
    return sortData(alertsData, alertsSortField, alertsSortDirection);
  };

  // Search function to fetch data with date range
  const handleSearch = async () => {
    if (!plugId) return;

    try {
      // Always use date range parameters for search functionality
      const dateParams = createDateRangeParams(fromDate, toDate, formatToApiDateTime);

      // Fetch both logs and alerts with date parameters
      await fetchAlertsData(plugId, dateParams);

    } catch (err) {
      // Error handling for date range search - could set error state if needed
      setLogsError(err.message);
      setAlertsError(err.message);
    }
  };

  // Refresh data when date range changes
  // const refreshDataWithDateRange = () => {
  //   if (plugId) {
  //     const dateParams = {
  //       from_time: `${fromDate} 00:00:00`,
  //       to_time: `${toDate} 23:59:59`
  //     };
  //     fetchLogsData(plugId, dateParams);
  //     fetchAlertsData(plugId, dateParams);
  //   }
  // };

  // Show loading state
  if (loading) {
    return (
      <PlugDrawerContainer
        anchor="right"
        open={true}
        onClose={onClose}
        disableScrollLock={false}
        keepMounted={false}
        slotProps={{
          backdrop: {
            sx: {
              opacity: 0.5,
              pointerEvents: 'none', // Disable user interaction
            },
          },
        }}
      >
        <PlugDrawerHeader>
          <Box>
            <PlugDrawerTitle variant="caption">
              {AppLabels.plugDrawerLabels.drawerTitle}
            </PlugDrawerTitle>
            <PlugDrawerDeviceName variant="h5" component="h3">
              Loading...
            </PlugDrawerDeviceName>
          </Box>

          <PlugDrawerSecondaryButton onClick={onClose}>
            <CloseIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
            {AppLabels.plugDrawerLabels.closeDrawer}
          </PlugDrawerSecondaryButton>
        </PlugDrawerHeader>
        <PlugDrawerContent>
          <PlugDrawerTabContentPanel sx={{ textAlign: 'center' }}>
            <PlugDrawerLoadingMessage>
              <PlugDrawerSpinner sx={{ marginRight: '8px' }} />
              {AppLabels.plugDrawerLabels.loadingLogs}
            </PlugDrawerLoadingMessage>
          </PlugDrawerTabContentPanel>
        </PlugDrawerContent>
      </PlugDrawerContainer>
    );
  }

  return (
    <PlugDrawerContainer
      anchor="right"
      open={true}
      onClose={onClose}
      disableScrollLock={false}
      keepMounted={false}
      slotProps={{
        backdrop: {
          sx: {
            opacity: 0.5,
            pointerEvents: 'none', // Disable user interaction
          },
        },
      }}
    >
      <PlugDrawerHeader>
        <Box>
          <PlugDrawerTitle variant="caption">
            {AppLabels.plugDrawerLabels.drawerTitle}
          </PlugDrawerTitle>
          <PlugDrawerDeviceName variant="h5" component="h3">
            {plugDetails?.deviceName || AppLabels.notApplicable}
          </PlugDrawerDeviceName>
        </Box>

        <PlugDrawerSecondaryButton onClick={onClose}>
          <CloseIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
          {AppLabels.plugDrawerLabels.closeDrawer}
        </PlugDrawerSecondaryButton>
      </PlugDrawerHeader>

      <PlugDrawerContent>
        {/* Show error message if device details failed to load */}
        {error && (
          <PlugDrawerErrorBox>
            <PlugDrawerErrorHeader>
              <Typography className="error-icon">⚠️</Typography>
              <Typography className="error-title">
                {AppLabels.plugDrawerLabels.deviceDetailsError}
              </Typography>
            </PlugDrawerErrorHeader>
            <PlugDrawerErrorMessage>
              {error || AppLabels.plugDrawerLabels.failedToLoadCompleteDetails}
            </PlugDrawerErrorMessage>
            <PlugDrawerPrimaryButton
              onClick={() => {
                // Use specific method based on advanced search state
                const dateParams = showAdvancedSearch
                  ? createDateRangeParams(fromDate, toDate, formatToApiDateTime)
                  : {};
                fetchAlertsData(plugId, dateParams);
              }}
              sx={{ marginTop: '8px' }}
            >
              {AppLabels.plugDrawerLabels.retryButton}
            </PlugDrawerPrimaryButton>
            <PlugDrawerErrorHelp>
              {AppLabels.plugDrawerLabels.showingBasicInfoFromCache}
            </PlugDrawerErrorHelp>
          </PlugDrawerErrorBox>
        )}

        {plugDetails && (
          <PlugDrawerMetaSection>
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.plugIdLabel}
              value={plugDetails.id}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.deviceNameLabel}
              value={plugDetails.deviceName}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.siteLabel}
              value={plugDetails.siteId}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.locationLabel}
              value={plugDetails.locationId}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.deviceMacLabel}
              value={<DashboardMonospaceText>{plugDetails.deviceMac}</DashboardMonospaceText>}
            />
             <PlugMetadataField
              label={AppLabels.plugDrawerLabels.statusLabel}
              value={plugDetails.status}
              isStatus={true}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.modemIdLabel}
              value={<DashboardMonospaceText>{plugDetails.modemId}</DashboardMonospaceText>}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.smartPlugLocationLabel}
              value={formattedText(plugDetails.smartPlugLocation)}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.singleSourceDevicesLabel}
              value={renderCriticalDeviceList(plugDetails.criticalDevices)}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.dualSourceDevicesDeviceLabel}
              value={renderCriticalDeviceList(plugDetails.dualSupplyDevices)}
            />
            <PlugMetadataField
              label={AppLabels.plugDrawerLabels.lastHeartbeatLabel}
              value={plugDetails.lastheartbeatat}
            />
          </PlugDrawerMetaSection>
        )}

        {/* Advanced Search Toggle */}
        <PlugDrawerAdvancedSearchContainer>
          <PlugDrawerAdvancedSearchToggle
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            startIcon={showAdvancedSearch ? '▼' : '▶'}
          >
            {AppLabels.plugDrawerLabels.advancedSearchToggle}
          </PlugDrawerAdvancedSearchToggle>
        </PlugDrawerAdvancedSearchContainer>

        {/* Date Range Filters - Conditionally Rendered */}
        {showAdvancedSearch && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <PlugDrawerDateFiltersPanel>
              <PlugDrawerDateFiltersContainer>
                <PlugDrawerDateFiltersLeft>
                  {/* Date Pickers Row Container */}

                  <Box className="date-field-container">
                    <PlugDrawerDateFieldLabel variant="caption">
                      {AppLabels.plugDrawerLabels.fromDateTimeLabel}
                    </PlugDrawerDateFieldLabel>
                    <DateTimePicker

                      value={fromDate ? dayjs(fromDate) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          const formattedDate = formatToLocalDateTime(newValue.toDate());
                          handleFromDateChange(formattedDate);
                        }
                      }}
                      maxDateTime={dayjs()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!dateRangeError,
                          sx: {
                            width: '230px',
                            minWidth: '200px',
                            '& .MuiInputBase-root': {
                              height: '40px',
                            }
                          }
                        },
                        actionBar: {
                          actions: ['accept', 'cancel', 'today'],
                        },
                      }}
                    />
                  </Box>

                  <Box className="date-field-container">
                    <PlugDrawerDateFieldLabel variant="caption">
                      {AppLabels.plugDrawerLabels.toDateTimeLabel}
                    </PlugDrawerDateFieldLabel>
                    <DateTimePicker

                      value={toDate ? dayjs(toDate) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          const formattedDate = formatToLocalDateTime(newValue.toDate());
                          handleToDateChange(formattedDate);
                        }
                      }}
                      maxDateTime={dayjs()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!dateRangeError,
                          sx: {
                            width: '230px',
                            minWidth: '200px',
                            '& .MuiInputBase-root': {
                              height: '40px',
                            }
                          }
                        },
                        actionBar: {
                          actions: ['accept', 'cancel', 'today'],
                        },
                      }}
                    />
                  </Box>

                  {/* Search Button Container */}
                  <Box sx={{
                    alignSelf: 'flex-end',
                    marginTop: '20px',
                    marginLeft: '6px'
                  }}>
                    <PlugDrawerPrimaryButton
                      onClick={handleSearch}
                      disabled={!!dateRangeError}
                      startIcon={<SearchIcon />}
                      sx={{
                        minWidth: '130px',
                        height: '40px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {AppLabels.plugDrawerLabels.searchButton}
                    </PlugDrawerPrimaryButton>
                  </Box>
                  {/* Error Message Display */}
                  {dateRangeError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        display: 'block',
                        marginTop: 1,
                        marginLeft: 1.75,
                        fontSize: '0.75rem'
                      }}
                    >
                      {dateRangeError}
                    </Typography>
                  )}
                </PlugDrawerDateFiltersLeft>

                <PlugDrawerDateFiltersRight>
                  <PlugDrawerRangeText>
                    {AppLabels.plugDrawerLabels.rangePrefix}{new Date(fromDate).toLocaleString()}{AppLabels.plugDrawerLabels.rangeArrow}{new Date(toDate).toLocaleString()}
                  </PlugDrawerRangeText>
                  {/* <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <PlugDrawerPrimaryButton
                      onClick={handleSearch}
                      disabled={!!dateRangeError}
                      startIcon={<SearchIcon />}
                    >
                      {AppLabels.plugDrawerLabels.searchButton}
                    </PlugDrawerPrimaryButton>
                     <div style={{ 
                  fontSize: '0.75rem',
                  color: FORD_COLORS.textMuted,
                  fontStyle: 'italic',
                  marginTop: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  * For Logs showing only top 100 records
                </div> 
                  </div> */}
                </PlugDrawerDateFiltersRight>
              </PlugDrawerDateFiltersContainer>
            </PlugDrawerDateFiltersPanel>
          </LocalizationProvider>
        )}

        {/* Tab Navigation */}
        {/* <StyledTabNavigation>
          <div style={{ display: 'flex', gap: '16px' }}>
            <StyledTabButton 
              onClick={() => setActiveTab('logs')}
              active={activeTab === 'logs'}
            >
              Logs
            </StyledTabButton>
            <StyledTabButton 
              onClick={() => setActiveTab('alerts')}
              active={activeTab === 'alerts'}
            >
              Alerts
            </StyledTabButton>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-end', 
            fontSize: '0.75rem',
            color: FORD_COLORS.textMuted,
            fontStyle: 'italic'
          }}>
            {activeTab === 'logs' && (
              <div>
                * For Logs showing only top 100 records
              </div>
            )}
          </div>
        </StyledTabNavigation> */}

        {/* Logs Section */}
        {activeTab === 'logs' && (
          <PlugDrawerTabContentPanel>
            <PlugDrawerTabContentHeader>
              <Typography variant="h6" component="h4">
                {AppLabels.plugDrawerLabels.deviceLogsHeader}
              </Typography>
              <PlugDrawerPrimaryButton
                onClick={() => {
                  // Use specific method for logs refresh
                  const dateParams = showAdvancedSearch
                    ? createDateRangeParams(fromDate, toDate, formatToApiDateTime)
                    : {};
                  fetchLogsData(plugId, dateParams);
                }}
                disabled={logsLoading || !!dateRangeError}
              >
                {logsLoading ? AppLabels.plugDrawerLabels.loadingDotsText : AppLabels.plugDrawerLabels.refreshLogsButton}
              </PlugDrawerPrimaryButton>
            </PlugDrawerTabContentHeader>

            {logsError && (
              <ErrorDisplay
                error={logsError}
                noDataText="No logs found"
                errorPrefix="Error loading logs: "
              />
            )}

            {logsLoading && (
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                <PlugDrawerLoadingMessage>
                  <PlugDrawerSpinner sx={{ marginRight: '8px' }} />
                  {AppLabels.plugDrawerLabels.loadingLogs}
                </PlugDrawerLoadingMessage>
              </Box>
            )}

            {!logsLoading && logsError === null && (
              <PlugDrawerTableWrapper>
                <table>
                  <thead>
                    <tr>
                      <SortableHeader
                        field="received_date"
                        sortField={logsSortField}
                        sortDirection={logsSortDirection}
                        onSort={handleLogsSort}
                      >
                        Received Date
                      </SortableHeader>
                      {/* <SortableHeader 
                        field="log_type" 
                        sortField={logsSortField} 
                        sortDirection={logsSortDirection} 
                        onSort={handleLogsSort}
                      >
                        Type
                      </SortableHeader> */}
                      <SortableHeader
                        field="payload"
                        sortField={logsSortField}
                        sortDirection={logsSortDirection}
                        onSort={handleLogsSort}
                        style={{
                          width: '400px',
                          minWidth: '400px',
                          maxWidth: '400px'
                        }}
                      >
                        Payload
                      </SortableHeader>
                      {/* <SortableHeader 
                        field="severity" 
                        sortField={logsSortField} 
                        sortDirection={logsSortDirection} 
                        onSort={handleLogsSort}
                      >
                        Severity
                      </SortableHeader> */}
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedLogsData().slice((logsPage - 1) * logsPageSize, logsPage * logsPageSize).map((log, i) => (
                      <tr key={log.id || log.received_date || `log-${i}`}>
                        <td>{DateUtils.formatDateWithLocalZone(log.received_date) || AppLabels.notApplicable}</td>
                        {/* <td>
                          <PlugDrawerStatusChip 
                            label={log.log_type} 
                            chiptype={log.log_type === 'ERROR' ? 'chip-error' : log.log_type === 'WARNING' ? 'chip-warn' : 'chip-ok'}
                            size="small"
                          />
                        </td> */}
                        <td style={{
                          width: '400px',
                          minWidth: '400px',
                          maxWidth: '400px',
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          padding: '8px',
                          verticalAlign: 'top'
                        }}>
                          {log.payload || AppLabels.notApplicable}
                        </td>
                        {/* <td>
                          <PlugDrawerStatusChip 
                            label={log.severity} 
                            chiptype={log.severity === 'high' ? 'chip-error' : log.severity === 'medium' ? 'chip-warn' : 'chip-ok'}
                            size="small"
                          />
                        </td> */}
                      </tr>
                    ))}
                    {logsData.length === 0 && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', color: '#9aa3c7', padding: '20px' }}>
                          No logs available for this device
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </PlugDrawerTableWrapper>
            )}

            {/* Logs Pagination */}
            {logsError === null && !logsLoading && getSortedLogsData().length > logsPageSize && (
              <PaginationControls
                currentPage={logsPage}
                setPage={setLogsPage}
                data={getSortedLogsData()}
                pageSize={logsPageSize}
              />
            )}
          </PlugDrawerTabContentPanel>
        )}

        {/* Alerts Section */}
        {activeTab === 'alerts' && (
          <PlugDrawerTabContentPanel>
            <PlugDrawerTabContentHeader>
              <Typography variant="h6" component="h4">
                {AppLabels.plugDrawerLabels.deviceAlertsHeader}
              </Typography>
              <PlugDrawerPrimaryButton
                onClick={() => {
                  // Use specific method for alerts refresh
                  const dateParams = showAdvancedSearch
                    ? createDateRangeParams(fromDate, toDate, formatToApiDateTime)
                    : {};
                  fetchAlertsData(plugId, dateParams);
                }}
                disabled={alertsLoading || !!dateRangeError}
              >
                {alertsLoading ? AppLabels.plugDrawerLabels.loadingDotsText : AppLabels.plugDrawerLabels.refreshAlertsButtonDynamic}
              </PlugDrawerPrimaryButton>
            </PlugDrawerTabContentHeader>

            {alertsError && (
              <ErrorDisplay
                error={alertsError}
                noDataText="No alerts found"
                errorPrefix=''
              />
            )}

            {alertsLoading && (
              <Box sx={{ textAlign: 'center', padding: '10px' }}>
                <PlugDrawerLoadingMessage>
                  <PlugDrawerSpinner sx={{ marginRight: '8px' }} />
                  {AppLabels.plugDrawerLabels.loadingAlertsText}
                </PlugDrawerLoadingMessage>
              </Box>
            )}

            {!alertsLoading && alertsError === null && (
              <PlugDrawerTableWrapper>
                <table>
                  <thead>
                    <tr>
                      <SortableHeader
                        field="incident_id"
                        sortField={alertsSortField}
                        sortDirection={alertsSortDirection}
                        onSort={handleAlertsSort}
                      >
                        {AppLabels.plugDrawerLabels.incidentIdHeader}
                      </SortableHeader>
                      <SortableHeader
                        field="alert_type"
                        sortField={alertsSortField}
                        sortDirection={alertsSortDirection}
                        onSort={handleAlertsSort}
                      >
                        {AppLabels.plugDrawerLabels.alertTypeHeader}
                      </SortableHeader>
                      <SortableHeader
                        field="reported_time"
                        sortField={alertsSortField}
                        sortDirection={alertsSortDirection}
                        onSort={handleAlertsSort}
                      >
                        {AppLabels.plugDrawerLabels.reportedTimeHeaderAlt}
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedAlertsData().slice((alertsPage - 1) * alertsPageSize, alertsPage * alertsPageSize).map((alert, i) => (
                      <tr key={alert.incident_id || alert.reported_time || `alert-${i}`}>
                        <td style={{ fontFamily: 'monospace', fontSize: STYLE_CONFIG.FONT_SIZE_SMALL, fontWeight: 'bold' }}>{alert.incident_id}</td>
                        <td>
                          <StatusIndicator
                                  status={alert.alert_type.toLowerCase().replaceAll(/\b\w/g, l => l.toUpperCase())}
                                  size='full'
                                />
                        
                          </td>
                        <td>{DateUtils.formatDateWithLocalZone(alert.reported_time) || AppLabels.notApplicable}</td>
                      </tr>
                    ))}
                    {alertsData.length === 0 && (
                      <tr>
                        <td colSpan={STYLE_CONFIG.COLSPAN_FULL} style={{ textAlign: 'center', color: 'black', padding: STYLE_CONFIG.PADDING_MEDIUM }}>
                          {AppLabels.plugDrawerLabels.noAlertsAvailable}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </PlugDrawerTableWrapper>
            )}

            {/* Alerts Pagination */}
            {alertsError === null && !alertsLoading && getSortedAlertsData().length > alertsPageSize && (
              <PaginationControls
                currentPage={alertsPage}
                setPage={setAlertsPage}
                data={getSortedAlertsData()}
                pageSize={alertsPageSize}
              />
            )}
          </PlugDrawerTabContentPanel>
        )}
      </PlugDrawerContent>
    </PlugDrawerContainer>
  );
};

// PropTypes validation
PlugDrawer.propTypes = {
  plugId: PropTypes.string.isRequired,
  plugDetails: PropTypes.shape({
    id: PropTypes.string,
    siteId: PropTypes.string,
    locationId: PropTypes.string,
    deviceMac: PropTypes.string,
    status: PropTypes.string,
    model: PropTypes.string,
    isModem: PropTypes.bool,
    modemId: PropTypes.string,
    ip: PropTypes.string,
    criticalDeviceList: PropTypes.string,
    lastheartbeatat: PropTypes.string,
    smartPlugLocation: PropTypes.string,
    deviceName: PropTypes.string,
    criticalDevices: PropTypes.array,
    dualSupplyDevices: PropTypes.array,
  }),
  onClose: PropTypes.func.isRequired
};

// Default props to handle optional parameters
PlugDrawer.defaultProps = {
  plugDetails: {
    id: '',
    siteId: '',
    locationId: '',
    deviceMac: '',
    status: '',
    model: '',
    isModem: false,
    modemId: '',
    ip: '',
    criticalDeviceList: '',
    lastheartbeatat: '',
    smartPlugLocation: '',
    deviceName: '',
    criticalDevices: [],
    dualSupplyDevices: [],
  }
};

export default PlugDrawer;
